// =============================================================================
// Landing — Variant A (Momentum-first)
// =============================================================================
// The goal card (150,000 target — no live headcount) is the hero, About section
// below, big Join CTA at the bottom.
// Per BRD §10. The map embed lands in Phase 5.
// =============================================================================

import { useNavigate, Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Logo } from "@/components/brand/Logo"
import { GoalCard } from "@/components/brand/GoalCard"
import { IsraelMap } from "@/components/brand/IsraelMap"
import { assetUrl } from "@/lib/utils"
import { ArrowLeft } from "lucide-react"

// -- Content ported from elhadegel.co.il (the movement's official site) -------
const LEADERS = [
  {
    name: "מתן יפה",
    title: "יו״ר התנועה",
    location: "שריגים",
    bio: "יזם חברתי, מייסד עמותת ״כוכבי המדבר״ למנהיגות בחברה הבדואית. רס״ן במיל׳ בשריון, מוביל תחום הביטחון בתנועה.",
    photo: "team/matan-yefe.jpg",
  },
  {
    name: "אלי מאירי",
    title: "מוביל תחום החינוך והשטח",
    location: "מושב ארבל",
    bio: "איש חינוך, לשעבר מנהל אגף החינוך בעיריית טבריה. סא״ל במיל׳ בשריון, מוביל תחום החינוך והשטח בתנועה.",
    photo: "team/eli-meiri.jpg",
  },
  {
    name: "עדן בן עוז קוהלי",
    title: "מובילת קהילת הנשים",
    location: "הרצליה",
    bio: "מייסדת ומנהלת ״מתכון עם זיכרון״, מיזם הנצחה לחללי מערכות ישראל באמצעות בישול. רס״ן במיל׳ ביחידה מיוחדת, מעל 500 ימי מילואים מאז 7 באוקטובר. מובילה את הקמת קהילת הנשים של התנועה.",
    photo: "team/eden-ben-oz-kohli.jpg",
  },
  {
    name: "דוד שרז",
    title: "מוביל תחום הכלכלה",
    location: "רמת השרון",
    bio: "יזם הייטק ופעיל חברתי. רס״ן במיל׳ ביחידה מובחרת, מוביל תחום הכלכלה בתנועה.",
    photo: "team/david-sharaz.jpg",
  },
  {
    name: "רעות פרנקל",
    title: "מובילת תחום התוכן והקהילה",
    location: "ירושלים",
    bio: "אשת חינוך, תוכן וקהילה. מנהלת שותפויות ופיתוח תוכניות, ממובילות תחום התוכן, ההסברה ובניית הקהילה בתנועה.",
    photo: "team/reut-frankel.jpg",
  },
  {
    name: "יצחקי גליק",
    title: "מוביל תחום הממשל",
    location: "מזכרת בתיה",
    bio: "עו״ד ויזם נדל״ן ביהודה ושומרון. רס״ן במיל׳ בחטיבת הקומנדו, מוביל תחום הממשל בתנועה.",
    photo: "team/yitzchaki-glick.jpg",
  },
]

const FLAGS = [
  {
    name: "דגל הביטחון",
    line: "ממדינה מגיבה - למדינה יוזמת ומכריעה",
    icon: "flags/bitachon.svg",
  },
  {
    name: "דגל הכלכלה",
    line: "מכלכלת אינטרסים וקבוצות לחץ - למעצמה של כלכלה לאומית",
    icon: "flags/calcla.svg",
  },
  {
    name: "דגל החינוך והלכידות",
    line: "מחינוך מגזרי ומסורבל - למערכת חינוך אחת מחייבת, רזה ומאפשרת",
    icon: "flags/hinuch.svg",
  },
  {
    name: "דגל הממשל",
    line: "ממערכת ממשל מסוכסכת ותקועה - למערכת מאוזנת עם גבולות מוגדרים",
    icon: "flags/mishpat.svg",
  },
]

const CREED = [
  "אם אמונתי היא שמדינת ישראל היא הבית של העם היהודי.",
  "אם המדינה הזאת אהובה עליי בכל ליבי.",
  "אם לעולם לא אהסס להגן עליה, ללא תנאים.",
  "אם ברור לי שטובת המדינה עומדת מעל לאינטרסים אישיים ולמחלוקות פוליטיות של שמאל וימין.",
  "אם אני רוצה מדינה שמכריעה את אויביה ומושיטה יד לשלום.",
  "מדינה גאה ביהדותה שמעניקה חופש דת.",
  "מדינה שכל אזרחיה משרתים אותה ושומרים על אמונתם.",
]

export function HomePage() {
  const navigate = useNavigate()
  return (
    <div className="min-h-screen bg-white text-[#0F1B36]">
      <header className="border-b border-[#E5E7EB]/70 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={() => navigate("/welcome")}>
              הצטרף
              <ArrowLeft className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      <section className="border-b border-[#E5E7EB]/70 bg-gradient-to-b from-white to-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1B2D52]/5 px-4 py-1.5 mb-6 ring-1 ring-[#1B2D52]/10">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1B2D52]" />
              <span className="text-xs font-semibold text-[#1B2D52] tracking-wide">
                פרויקט 150,000 · קמפיין 2026
              </span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.95] text-balance">
              אל הדגל
            </h1>
            <p className="mt-5 text-xl lg:text-2xl font-medium text-[#1B2D52]/85 leading-relaxed text-balance">
              דור חדש של ציונות. תנועה אחת. מפה אחת.
              <br />
              כל מי שמצטרף — מוסיף את הדגל שלו.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start gap-4">
              <Button
                size="xl"
                onClick={() => navigate("/welcome")}
                className="group min-w-[200px] shadow-lg shadow-[#1B2D52]/20"
              >
                הצטרפו עכשיו
                <ArrowLeft className="size-5 transition-transform group-hover:-translate-x-1" />
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <GoalCard />
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-b border-[#E5E7EB]/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-12">
          <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
            <div>
              <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight text-[#0F1B36]">
                מפת התומכים בזמן אמת
              </h2>
              <p className="text-sm text-[#4B5563] mt-1">
                כל נקודה — תומכ/ה. הצבע מעיד על השלב שהדגל באזור הגיע אליו.
              </p>
            </div>
            <div className="text-xs text-[#6B7280] hidden md:block">
              נתונים: OpenStreetMap · CARTO
            </div>
          </div>
          <div className="rounded-2xl border border-[#1B2D52]/15 bg-white shadow-xl shadow-[#1B2D52]/5 overflow-hidden">
            <IsraelMap height="60vh" />
          </div>
        </div>
      </section>

      {/* המצע שלנו (platform / flags) */}
      <section className="bg-white border-t border-[#E5E7EB]/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1B2D52]/5 px-3 py-1 mb-4 ring-1 ring-[#1B2D52]/10">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#1B2D52]">
                המצע שלנו
              </span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0F1B36] leading-tight">
              ארבעה דגלים. כיוון אחד.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FLAGS.map((flag) => (
              <div
                key={flag.name}
                className="flex items-start gap-4 rounded-2xl border border-[#1B2D52]/12 bg-[#F8FAFC] p-6 transition-all hover:shadow-lg hover:shadow-[#1B2D52]/5 hover:border-[#1B2D52]/25"
              >
                <div className="shrink-0 grid place-items-center size-14 rounded-xl bg-[#1B2D52]/5 ring-1 ring-[#1B2D52]/10">
                  <img src={assetUrl(flag.icon)} alt="" className="size-8 object-contain" />
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-[#0F1B36]">{flag.name}</h3>
                  <p className="mt-1.5 text-[#4B5563] leading-relaxed">{flag.line}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* הכירו את מובילי אל הדגל (leaders) */}
      <section className="bg-[#F8FAFC] border-t border-[#E5E7EB]/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0F1B36] leading-tight">
              הכירו את מובילי אל הדגל
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {LEADERS.map((leader) => (
              <div key={leader.name} className="flex flex-col">
                <div className="aspect-[3/4] overflow-hidden rounded-2xl bg-[#E5E7EB] ring-1 ring-[#1B2D52]/10">
                  <img
                    src={assetUrl(leader.photo)}
                    alt={leader.name}
                    loading="lazy"
                    className="size-full object-cover object-top"
                  />
                </div>
                <h3 className="mt-4 text-lg font-extrabold text-[#0F1B36]">{leader.name}</h3>
                <span className="text-sm font-semibold text-[#1B2D52]">{leader.title}</span>
                <span className="text-xs text-[#6B7280] mt-0.5">{leader.location}</span>
                <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{leader.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ציונות - זה אני? (creed) */}
      <section className="bg-[#0F1B36] text-white border-t border-[#E5E7EB]/70">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24 text-center">
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight text-balance">
            ציונות - זה אני?
          </h2>
          <div className="mt-8 space-y-3 text-lg lg:text-xl text-white/80 leading-relaxed font-medium">
            {CREED.map((line) => (
              <p key={line} className="text-balance">
                {line}
              </p>
            ))}
          </div>
          <p className="mt-8 text-2xl lg:text-3xl font-extrabold text-white">
            אז ציונות היא אני.
          </p>
        </div>
      </section>

      {/* על אל הדגל — תנועה שנולדה מתוך השבעה באוקטובר */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#1B2D52]/5 px-3 py-1 mb-5 ring-1 ring-[#1B2D52]/10">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[#1B2D52]">
              על אל הדגל
            </span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0F1B36] leading-tight">
            תנועה שנולדה מתוך השבעה באוקטובר.
          </h2>
          <div className="mt-6 space-y-4 text-lg text-[#374151] leading-relaxed">
            <p>
              אל הדגל היא תנועה ציונית חדשה שנוסדה על ידי לוחמי מילואים, מחנכים, יזמים ואנשי
              ציבור — אנשים שחזרו מהמלחמה והבינו שצריך משהו אחר. לא ימין, לא שמאל. ארץ לפני
              מחנה.
            </p>
            <p>
              כדי להגיע לכנסת אנחנו צריכים{" "}
              <strong className="text-[#1B2D52]">150,000 תומכים</strong>. כל הצטרפות — דגל
              חדש על המפה.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#1B2D52] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20 text-center">
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight text-balance">
            מוכן/ה להניף את הדגל?
          </h2>
          <p className="mt-5 text-lg text-white/80 leading-relaxed max-w-2xl mx-auto text-balance">
            סרטון של דקה אחת, ואז אתה על המפה.
          </p>
          <div className="mt-9">
            <Button
              size="xl"
              variant="outline"
              onClick={() => navigate("/welcome")}
              className="group min-w-[220px] bg-white text-[#1B2D52] hover:bg-white/90 border-transparent"
            >
              כניסה והוספת הדגל שלך
              <ArrowLeft className="size-5 transition-transform group-hover:-translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E7EB] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col items-center gap-3 text-xs text-[#6B7280]">
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/privacy" className="hover:text-[#1B2D52]">מדיניות פרטיות</Link>
            <span className="opacity-40">·</span>
            <Link to="/terms" className="hover:text-[#1B2D52]">תנאי שימוש</Link>
            <span className="opacity-40">·</span>
            <Link to="/accessibility" className="hover:text-[#1B2D52]">הצהרת נגישות</Link>
          </nav>
          <span>© 2026 אל הדגל · כל הזכויות שמורות</span>
        </div>
      </footer>
    </div>
  )
}
